import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { theme } from '../styles/theme';

const testimonials = [
  {
    id: 1,
    name: 'Marcus Thompson',
    role: 'Content Creator',
    avatar: 'MT',
    text: "4KMatch has completely transformed my content game. My engagement has tripled since I started using it.",
  },
  {
    id: 2,
    name: 'Ryan Mitchell',
    role: 'Semi-Pro Footballer',
    avatar: 'RM',
    text: "I use 4KMatch to build my personal brand. Scouts and agents love the professional look of my profile.",
  },
  {
    id: 3,
    name: 'Sarah Chen',
    role: 'Football Agent',
    avatar: 'SC',
    text: "We use 4KMatch to create promo material for our players. It's become essential for showcasing talent.",
  },
  {
    id: 4,
    name: 'Emma Williams',
    role: 'Club Social Media Manager',
    avatar: 'EW',
    text: "Managing social for a club, 4KMatch helps us create fan engagement content in minutes, not hours.",
  },
  {
    id: 5,
    name: 'David Okonkwo',
    role: 'Football Fan',
    avatar: 'DO',
    text: "Finally I can see myself in my team's kit! Shared it with my mates and now they're all using it.",
  },
  {
    id: 6,
    name: 'Lisa Martinez',
    role: 'Marketing Director',
    avatar: 'LM',
    text: "We run campaigns for grassroots clubs. 4KMatch lets us deliver professional content on any budget.",
  },
];

// Existing quotes and uniform star ratings have no documented source in this repo.
// Owner verification is required before publication.
const wrap = (value: number, length: number) => ((value % length) + length) % length;
const ease = (value: number) => { const t = Math.max(0, Math.min(1, value)); return t * t * (3 - 2 * t); };

export const Testimonials: React.FC = () => {
  const section = useRef<HTMLElement>(null);
  const row = useRef<HTMLUListElement>(null);
  const offset = useRef(0);
  const hold = useRef(0);
  const slide = useRef<{ from: number; to: number; elapsed: number; manual: boolean } | null>(null);
  const drag = useRef<{ id: number; x: number; origin: number } | null>(null);
  const refresh = useRef<() => void>(() => {});
  const settle = useRef<(target: number) => void>(() => {});
  const previousWidth = useRef(0);
  const navigate = useRef<(delta: number) => void>(() => {});
  const [width, setWidth] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [inView, setInView] = useState(false);
  const [hidden, setHidden] = useState(document.hidden);
  const [reduced, setReduced] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const mobile = width <= 700;
  const cardWidth = mobile ? width * .78 : Math.max(300, width * .30);
  const pitch = cardWidth + 16;
  const active = inView && !hidden && !paused && !hovered && !focused && !reduced;

  useLayoutEffect(() => {
    const resize = () => setWidth(row.current?.clientWidth || 0);
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    if (row.current) observer?.observe(row.current);
    window.addEventListener('resize', resize);
    return () => { observer?.disconnect(); window.removeEventListener('resize', resize); };
  }, []);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotion = () => setReduced(motion.matches);
    const visibility = () => setHidden(document.hidden);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting), { threshold: .1 }
    );
    if (observer && section.current) observer.observe(section.current);
    else setInView(true);
    motion.addEventListener('change', updateMotion);
    document.addEventListener('visibilitychange', visibility);
    return () => { observer?.disconnect(); motion.removeEventListener('change', updateMotion); document.removeEventListener('visibilitychange', visibility); };
  }, []);

  useLayoutEffect(() => {
    const cards = Array.from(row.current?.children ?? []) as HTMLElement[];
    const paint = () => cards.forEach((card, index) => {
      // Six unique cards recycle only outside the viewport; there are no loop copies.
      const slot = wrap(index - offset.current + 2.5, testimonials.length) - 2.5;
      const x = (width - cardWidth) / 2 + slot * pitch;
      card.style.transform = `translateX(${x}px)`;
      card.setAttribute('aria-hidden', String(x >= width || x + cardWidth <= 0));
    });
    // A geometry change invalidates fractional travel and pointer coordinates.
    // Settle before paint, leaving the explicit pause preference untouched.
    if (previousWidth.current !== width || reduced) {
      offset.current = wrap(Math.round(offset.current), testimonials.length);
      slide.current = null;
      drag.current = null;
      if (previousWidth.current !== width) hold.current = 0;
      previousWidth.current = width;
    }
    let frame: number | null = null;
    let timer: number | null = null;
    let holdStarted: number | null = null;
    let previous = performance.now();
    const stop = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      if (timer !== null) window.clearTimeout(timer);
      if (holdStarted !== null) hold.current = Math.min(7000, hold.current + performance.now() - holdStarted);
      frame = null; timer = null; holdStarted = null;
    };
    const schedule = () => {
      if (drag.current || hidden || !inView || width === 0) return;
      const movement = slide.current;
      if (movement ? (movement.manual || active) : active) {
        if (movement || !mobile) {
          previous = performance.now();
          frame = window.requestAnimationFrame(tick);
        } else {
          holdStarted = performance.now();
          timer = window.setTimeout(() => {
            timer = null; holdStarted = null; hold.current = 7000;
            slide.current = { from: offset.current, to: Math.round(offset.current) + 1, elapsed: 0, manual: false };
            schedule();
          }, Math.max(0, 7000 - hold.current));
        }
      }
    };
    const tick = (now: number) => {
      frame = null;
      const elapsed = Math.min(64, Math.max(0, now - previous));
      const movement = slide.current;
      if (movement) {
        movement.elapsed += elapsed;
        const progress = Math.min(1, movement.elapsed / 750);
        offset.current = movement.from + (movement.to - movement.from) * ease(progress);
        if (progress === 1) {
          offset.current = wrap(movement.to, testimonials.length);
          slide.current = null; hold.current = 0;
        }
      } else offset.current = wrap(offset.current + elapsed / 24000, testimonials.length);
      paint();
      schedule();
    };
    settle.current = (target) => {
      stop();
      hold.current = 0;
      if (reduced) {
        offset.current = wrap(target, testimonials.length); slide.current = null;
      } else slide.current = { from: offset.current, to: target, elapsed: 0, manual: true };
      paint(); schedule();
    };
    navigate.current = delta => settle.current(Math.round(offset.current) + delta);
    refresh.current = () => { stop(); paint(); schedule(); };
    paint();
    schedule();
    return () => { stop(); refresh.current = () => {}; };
  }, [width, cardWidth, pitch, mobile, active, hidden, inView, reduced]);

  const finishDrag = (event: React.PointerEvent<HTMLUListElement>, cancelled = false) => {
    const gesture = drag.current;
    if (!gesture || gesture.id !== event.pointerId) return;
    drag.current = null;
    const distance = event.clientX - gesture.x;
    const dragged = gesture.origin - distance / pitch;
    const origin = Math.round(gesture.origin);
    let target = cancelled ? Math.round(offset.current) : Math.round(dragged);
    if (!cancelled) {
      if (Math.abs(distance) <= 35) target = origin;
      else if (target === origin) target = origin + (distance < 0 ? 1 : -1);
    }
    settle.current(target);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <section id="testimonials" ref={section} className="testimonials-rail-section" aria-labelledby="testimonials-heading"
      onFocusCapture={(event) => setFocused(event.target.matches(':focus-visible'))}
      onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false); }}>
      <div className="testimonials-rail-inner" style={paused ? { '--home-gradient-state': 'paused' } as React.CSSProperties : undefined}>
        <header className="testimonials-rail-heading">
          <h2 id="testimonials-heading"><span>Their matchday.</span>{' '}<span className="testimonials-heading-accent" data-home-gradient="text">Their words.</span></h2>
          <p>Discover how people use 4KMATCH to bring their football stories to life.</p>
        </header>
        <div className="testimonials-rail-viewport">
        <ul id="testimonials-carousel" className="testimonials-rail-row" ref={row}
          style={{ '--card-width': `${cardWidth}px` } as React.CSSProperties}
          aria-label={`Player testimonials. Left and right arrows browse; Space pauses or resumes automatic playback. Automatic playback ${paused ? 'paused' : 'enabled'}.`} aria-roledescription="carousel" tabIndex={0}
          onPointerEnter={(event) => { if (event.pointerType === 'mouse') setHovered(true); }} onPointerLeave={() => setHovered(false)}
          onKeyDown={(event) => {
            if (event.target === event.currentTarget && event.key === ' ') {
              event.preventDefault();
              if (!event.repeat) setPaused(value => !value);
              return;
            }
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
            event.preventDefault(); setFocused(true); navigate.current(event.key === 'ArrowRight' ? 1 : -1);
          }}
          onPointerDown={(event) => {
            if (!event.isPrimary || event.button !== 0) return;
            slide.current = null;
            drag.current = { id: event.pointerId, x: event.clientX, origin: offset.current };
            event.currentTarget.setPointerCapture(event.pointerId);
            refresh.current();
          }}
          onPointerMove={(event) => {
            if (drag.current?.id === event.pointerId && !reduced) {
              offset.current = drag.current.origin - (event.clientX - drag.current.x) / pitch;
              refresh.current();
            }
          }}
          onPointerUp={event => finishDrag(event)} onPointerCancel={event => finishDrag(event, true)}
          onLostPointerCapture={event => finishDrag(event, true)}
        >
          {testimonials.map(testimonial => (
            <li className="testimonials-rail-card" key={testimonial.id}>
              <div className="testimonials-rail-content">
                <div className="testimonials-rail-stars" role="img" aria-label="5 out of 5 stars">
                  {[1, 2, 3, 4, 5].map(star => <svg key={star} width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>)}
                </div>
                <blockquote className="testimonials-rail-quote">"{testimonial.text}"</blockquote>
                <div className="testimonials-rail-author">
                  <div className="testimonials-rail-avatar" aria-hidden="true">{testimonial.avatar}</div>
                  <div className="testimonials-rail-person"><h3>{testimonial.name}</h3><p>{testimonial.role}</p></div>
                </div>
              </div>
            </li>
          ))}
        </ul>
          <div className="testimonials-rail-edge testimonials-rail-edge-left" data-home-gradient="full" aria-hidden="true" />
          <div className="testimonials-rail-edge testimonials-rail-edge-right" data-home-gradient="full" aria-hidden="true" />
        </div>
      </div>
      <style>{STYLE}</style>
    </section>
  );
};

const STYLE = `
  #testimonials.testimonials-rail-section { padding: 32px 24px 24px; background: #000; color: #fff; font-family: ${theme.fonts.primary}; overflow: hidden; }
  #testimonials .testimonials-rail-inner { max-width: 1320px; min-width: 0; margin: 0 auto; }
  #testimonials .testimonials-rail-heading { position: relative; text-align: center; margin-bottom: 28px; padding: 0; }
  #testimonials .testimonials-rail-heading h2 { margin: 0; font-size: 36px; font-weight: 750; line-height: 1.2; letter-spacing: -.025em; }
  #testimonials .testimonials-rail-heading h2 > span { display: inline-block; }
  #testimonials .testimonials-heading-accent { background: ${theme.colors.gradientPrimary}; background-clip: text; -webkit-background-clip: text; color: transparent; }
  #testimonials .testimonials-rail-heading p { margin: 8px 0 0; font-size: 16px; color: #b9bdc6; line-height: 1.5; }
  #testimonials .testimonials-rail-row { display: grid; grid-template-columns: 100%; width: 100%; min-width: 0; list-style: none; margin: 0; padding: 4px 0 8px; overflow: hidden; touch-action: pan-y pinch-zoom; }
  #testimonials .testimonials-rail-card { grid-area: 1 / 1; position: relative; width: var(--card-width); min-width: 0; border: 3px solid #383334; border-radius: 18px; background: #fff; box-sizing: border-box; box-shadow: 0 2px 10px #0005; will-change: transform; }
  #testimonials .testimonials-rail-content { display: flex; flex-direction: column; height: 100%; box-sizing: border-box; padding: 24px; }
  #testimonials .testimonials-rail-stars { display: flex; gap: 3px; color: ${theme.colors.primary}; margin-bottom: 16px; }
  #testimonials .testimonials-rail-quote { margin: 0 0 24px; font-size: 16px; line-height: 1.65; color: #252833; overflow-wrap: break-word; }
  #testimonials .testimonials-rail-author { display: flex; align-items: center; gap: 12px; min-width: 0; margin-top: auto; }
  #testimonials .testimonials-rail-avatar { display: grid; place-items: center; flex: 0 0 40px; width: 40px; height: 40px; border: 1px solid #e1bdc9; border-radius: 50%; background: #fbeaf0; color: #a41146; font-size: 16px; font-weight: 600; }
  #testimonials .testimonials-rail-person { min-width: 0; overflow-wrap: break-word; }
  #testimonials .testimonials-rail-person h3 { margin: 0; font-size: 20px; font-weight: 650; line-height: 1.3; color: #151823; }
  #testimonials .testimonials-rail-person p { margin: 4px 0 0; font-size: 16px; line-height: 1.5; color: #545865; }
  #testimonials .testimonials-rail-viewport { position: relative; min-width: 0; isolation: isolate; }
  #testimonials .testimonials-rail-edge { position: absolute; top: 4px; bottom: 8px; width: clamp(24px, 7%, 92px); z-index: 1; pointer-events: none; background: ${theme.colors.gradientPrimary};
    -webkit-mask-image: linear-gradient(var(--edge-direction), #000 0%, #000 20%, transparent 100%), radial-gradient(ellipse 100% 80% at var(--edge-opening) 50%, transparent 35%, #000 100%);
    mask-image: linear-gradient(var(--edge-direction), #000 0%, #000 20%, transparent 100%), radial-gradient(ellipse 100% 80% at var(--edge-opening) 50%, transparent 35%, #000 100%);
    -webkit-mask-composite: source-in; mask-composite: intersect;
  }
  #testimonials .testimonials-rail-edge-left { left: 0; --edge-direction: to right; --edge-opening: 100%; --home-flow-delay: -1.46s; }
  #testimonials .testimonials-rail-edge-right { right: 0; --edge-direction: to left; --edge-opening: 0%; --home-flow-delay: -3.65s; }
  #testimonials .testimonials-rail-row:focus-visible { outline: 2px solid #ff3884; outline-offset: -2px; }
  @media (max-width: 700px) {
    #testimonials.testimonials-rail-section { padding: 24px 0 16px; }
    #testimonials .testimonials-rail-heading { margin-bottom: 24px; padding-left: 16px; padding-right: 16px; }
    #testimonials .testimonials-rail-heading h2 { font-size: 24px; }
    #testimonials .testimonials-rail-content { padding: 16px; }
    #testimonials .testimonials-rail-card { border-radius: 14px; }
    #testimonials .testimonials-rail-edge { width: 10%; }
  }
  @media (prefers-reduced-motion: reduce) { #testimonials .testimonials-rail-card { will-change: auto; } }
`;

export default Testimonials;
